import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Stack,
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Tooltip,
  TableSortLabel,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  getAllDriversCompliance,
  bulkImportDriversCsv,
  downloadAllDriversComplianceCsv,
  inviteDriver,
} from "api/onboardingApi";
import { unwrapList, unwrapObject } from "api/unwrap";

/**
 * /compliance/drivers — org-wide driver compliance master list.
 *
 * Every driver in tenant, with their required-training and required-policy
 * counts (union of universal-defaults + broker-required via active contracts),
 * onboarding step, and next-expiring item.
 *
 * Sortable by: name, company, completion, next expiry.
 * Filterable by: compliance status, onboarding step, search.
 */

const Kpi = ({ label, value, accent }) => (
  <Card variant="outlined" sx={{ borderRadius: 2, flex: 1, minWidth: 0 }}>
    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, color: accent || "#0f172a" }}>{value}</Typography>
    </CardContent>
  </Card>
);

const DAY_MS = 24 * 60 * 60 * 1000;

export const AllDriversCompliancePage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | compliant | gaps | not_ready
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importSendInvitations, setImportSendInvitations] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ firstName: "", lastName: "", phone: "", email: "" });
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [inviteSuccess, setInviteSuccess] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAllDriversCompliance();
      setRows(unwrapList(res));
    } catch (e) {
      setError(e?.message || "Failed to load drivers compliance");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const c = { total: rows.length, compliant: 0, gaps: 0, ready: 0, notReady: 0 };
    rows.forEach((r) => {
      if (r.fullyCompliant) c.compliant++;
      else c.gaps++;
      if (r.readyToDrive) c.ready++;
      else c.notReady++;
    });
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === "compliant" && !r.fullyCompliant) return false;
      if (filter === "gaps" && r.fullyCompliant) return false;
      if (filter === "not_ready" && r.readyToDrive) return false;
      if (!q) return true;
      const hay = `${r.fullName || ""} ${r.email || ""} ${r.phone || ""} ${r.customerCompanyName || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search, filter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let cmp = 0;
      switch (sortBy) {
        case "name":
          cmp = (a.fullName || "").localeCompare(b.fullName || "");
          break;
        case "company":
          cmp = (a.customerCompanyName || "").localeCompare(b.customerCompanyName || "");
          break;
        case "step":
          cmp = (a.onboardingStep || "").localeCompare(b.onboardingStep || "");
          break;
        case "completion":
          cmp = (a.completionPercent || 0) - (b.completionPercent || 0);
          break;
        case "expiry": {
          const ax = a.nextExpiryAt || Number.MAX_SAFE_INTEGER;
          const bx = b.nextExpiryAt || Number.MAX_SAFE_INTEGER;
          cmp = ax - bx;
          break;
        }
        default:
          cmp = 0;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortBy, sortDir]);

  const flipSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortBy(col);
      setSortDir("asc");
    }
  };

  const expiryLabel = (ms) => {
    if (!ms) return "—";
    const days = Math.floor((ms - Date.now()) / DAY_MS);
    return days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? "today" : `${days}d`;
  };

  return (
    <Stack spacing={2} sx={{ p: { xs: 2, md: 3 }, backgroundColor: "#f8fafc", minHeight: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#0f172a" }}>
            All Drivers — Compliance
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Every driver in tenant. Required items = universal defaults ∪ broker-specific (from each driver's company's active contracts).
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => {
              setInviteForm({ firstName: "", lastName: "", phone: "", email: "" });
              setInviteError(null);
              setInviteSuccess(null);
              setInviteOpen(true);
            }}
            sx={{ backgroundColor: "#1F4E78" }}
          >
            Invite Driver
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => {
              setImportFile(null);
              setImportResult(null);
              setImportError(null);
              setImportOpen(true);
            }}
          >
            Bulk Import CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={async () => {
              try {
                await downloadAllDriversComplianceCsv();
              } catch (e) {
                setError(e?.message || "CSV export failed");
              }
            }}
          >
            Export CSV
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      {error ? <Alert severity="error" onClose={() => setError(null)}>{error}</Alert> : null}

      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Kpi label="Total drivers" value={counts.total} />
        <Kpi label="Fully compliant" value={counts.compliant} accent="#16a34a" />
        <Kpi label="With gaps" value={counts.gaps} accent={counts.gaps > 0 ? "#dc2626" : "#0f172a"} />
        <Kpi label="Ready to drive" value={counts.ready} accent="#3b82f6" />
      </Box>

      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        <TextField
          size="small"
          placeholder="Search name / email / phone / company"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 240 }}
        />
        {[
          { k: "all", label: "All" },
          { k: "compliant", label: "Compliant" },
          { k: "gaps", label: "Gaps" },
          { k: "not_ready", label: "Not ready" },
        ].map((b) => (
          <Chip
            key={b.k}
            size="small"
            label={b.label}
            onClick={() => setFilter(b.k)}
            sx={{
              backgroundColor: filter === b.k ? "#1F4E78" : "#f1f5f9",
              color: filter === b.k ? "#ffffff" : "#475569",
              fontWeight: filter === b.k ? 700 : 500,
              cursor: "pointer",
            }}
          />
        ))}
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ "& th": { fontWeight: 700, backgroundColor: "#f8fafc" } }}>
              <TableCell>
                <TableSortLabel
                  active={sortBy === "name"}
                  direction={sortBy === "name" ? sortDir : "asc"}
                  onClick={() => flipSort("name")}
                >
                  Driver
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === "company"}
                  direction={sortBy === "company" ? sortDir : "asc"}
                  onClick={() => flipSort("company")}
                >
                  Company
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={sortBy === "step"}
                  direction={sortBy === "step" ? sortDir : "asc"}
                  onClick={() => flipSort("step")}
                >
                  Step
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">Trainings</TableCell>
              <TableCell align="center">Policies</TableCell>
              <TableCell align="center">Docs verified</TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={sortBy === "expiry"}
                  direction={sortBy === "expiry" ? sortDir : "asc"}
                  onClick={() => flipSort("expiry")}
                >
                  Next expiry
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} sx={{ textAlign: "center", py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                  {rows.length === 0
                    ? "No drivers in tenant yet."
                    : "No drivers match the current filter."}
                </TableCell>
              </TableRow>
            ) : (
              sorted.map((r) => {
                const tFull = r.completedTrainingCount === r.requiredTrainingCount;
                const pFull = r.signedPolicyCount === r.requiredPolicyCount;
                const expiryDays = r.nextExpiryAt
                  ? Math.floor((r.nextExpiryAt - Date.now()) / DAY_MS)
                  : null;
                return (
                  <TableRow key={r.driverDetailsId} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.fullName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {r.email || r.phone || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{r.customerCompanyName || "—"}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={r.onboardingStep ? r.onboardingStep.replace(/_/g, " ") : "—"}
                        sx={{ height: 20, fontSize: 11 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: tFull ? "#16a34a" : "#dc2626" }}
                      >
                        {r.completedTrainingCount} / {r.requiredTrainingCount}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: pFull ? "#16a34a" : "#dc2626" }}
                      >
                        {r.signedPolicyCount} / {r.requiredPolicyCount}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{r.verifiedDocumentCount}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      {r.nextExpiryAt ? (
                        <Tooltip title={new Date(r.nextExpiryAt).toLocaleDateString()}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color:
                                expiryDays < 0
                                  ? "#dc2626"
                                  : expiryDays <= 30
                                  ? "#f97316"
                                  : "#475569",
                            }}
                          >
                            {expiryLabel(r.nextExpiryAt)}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={r.fullyCompliant ? "Compliant" : "Gaps"}
                        sx={{
                          backgroundColor: r.fullyCompliant ? "#dcfce7" : "#fee2e2",
                          color: r.fullyCompliant ? "#166534" : "#991b1b",
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        onClick={() => navigate(`/compliance/drivers/${r.driverDetailsId}`)}
                        endIcon={<OpenInNewIcon fontSize="small" />}
                      >
                        Open
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Invite a driver</DialogTitle>
        <DialogContent dividers>
          {inviteError ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setInviteError(null)}>{inviteError}</Alert> : null}
          {inviteSuccess ? (
            <Alert severity="success">{inviteSuccess}</Alert>
          ) : (
            <Stack spacing={2}>
              <Alert severity="info">
                Creates the driver account and emails a "complete your profile" link to the address below. Use Bulk Import CSV for phone-only drivers.
              </Alert>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  size="small"
                  label="First name"
                  value={inviteForm.firstName}
                  onChange={(e) => setInviteForm({ ...inviteForm, firstName: e.target.value })}
                  sx={{ flex: 1 }}
                  required
                />
                <TextField
                  size="small"
                  label="Last name"
                  value={inviteForm.lastName}
                  onChange={(e) => setInviteForm({ ...inviteForm, lastName: e.target.value })}
                  sx={{ flex: 1 }}
                  required
                />
              </Box>
              <TextField
                size="small"
                label="Phone"
                value={inviteForm.phone}
                onChange={(e) => setInviteForm({ ...inviteForm, phone: e.target.value })}
                fullWidth
                required
              />
              <TextField
                size="small"
                label="Email"
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                fullWidth
                required
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {inviteSuccess ? (
            <Button
              variant="contained"
              onClick={() => {
                setInviteOpen(false);
                load();
              }}
            >
              Done
            </Button>
          ) : (
            <>
              <Button onClick={() => setInviteOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                disabled={
                  inviting ||
                  !inviteForm.firstName.trim() ||
                  !inviteForm.lastName.trim() ||
                  !inviteForm.phone.trim() ||
                  !inviteForm.email.trim()
                }
                startIcon={inviting ? <CircularProgress size={16} /> : null}
                sx={{ backgroundColor: "#1F4E78" }}
                onClick={async () => {
                  setInviting(true);
                  setInviteError(null);
                  try {
                    await inviteDriver(inviteForm);
                    setInviteSuccess(`Invitation sent to ${inviteForm.email}`);
                  } catch (e) {
                    setInviteError(e?.response?.data?.message || e?.message || "Invite failed");
                  } finally {
                    setInviting(false);
                  }
                }}
              >
                Send invitation
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={importOpen} onClose={() => setImportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Bulk import drivers from CSV</DialogTitle>
        <DialogContent dividers>
          {importError ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setImportError(null)}>{importError}</Alert> : null}

          {importResult ? (
            <Stack spacing={1.5}>
              <Alert severity={importResult.failed > 0 ? "warning" : "success"}>
                {importResult.totalRows} row(s) processed —
                {" "}{importResult.created} created
                {importResult.skipped > 0 ? `, ${importResult.skipped} skipped (already exists)` : ""}
                {importResult.failed > 0 ? `, ${importResult.failed} failed` : ""}.
              </Alert>
              <Box sx={{ maxHeight: 320, overflow: "auto", border: "1px solid #e2e8f0", borderRadius: 1, p: 1 }}>
                {importResult.rows?.map((r) => (
                  <Box key={r.lineNumber} sx={{ display: "flex", gap: 1, py: 0.25 }}>
                    <Typography variant="caption" sx={{ minWidth: 30, color: "#94a3b8" }}>L{r.lineNumber}</Typography>
                    <Chip
                      size="small"
                      label={r.outcome}
                      sx={{
                        height: 18,
                        fontSize: 10,
                        fontWeight: 700,
                        backgroundColor: r.outcome === "CREATED" ? "#dcfce7" : r.outcome === "SKIPPED_DUPLICATE" ? "#fef3c7" : "#fee2e2",
                        color: r.outcome === "CREATED" ? "#166534" : r.outcome === "SKIPPED_DUPLICATE" ? "#92400e" : "#991b1b",
                      }}
                    />
                    <Typography variant="caption" sx={{ flex: 1 }}>
                      {r.firstName} {r.lastName} · {r.phone}{r.email ? ` · ${r.email}` : ""}
                    </Typography>
                    {r.message && r.outcome !== "CREATED" ? (
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                        {r.message}
                      </Typography>
                    ) : null}
                  </Box>
                ))}
              </Box>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Alert severity="info">
                CSV must have a header row with columns: <strong>firstName</strong>, <strong>lastName</strong>, <strong>phone</strong>, and optionally <strong>email</strong>.
                Phone numbers can be in any format — they'll be normalized to ###-###-####.
              </Alert>
              <Box>
                <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
                  Choose CSV file
                  <input
                    type="file"
                    hidden
                    accept=".csv,text/csv"
                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  />
                </Button>
                {importFile ? (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Selected: <strong>{importFile.name}</strong> ({Math.round(importFile.size / 1024)} KB)
                  </Typography>
                ) : null}
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={importSendInvitations}
                    onChange={(e) => setImportSendInvitations(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    Email invitations to drivers with an email address
                  </Typography>
                }
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {importResult ? (
            <Button
              onClick={() => {
                setImportOpen(false);
                load();
              }}
              variant="contained"
            >
              Done
            </Button>
          ) : (
            <>
              <Button onClick={() => setImportOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                disabled={!importFile || importing}
                startIcon={importing ? <CircularProgress size={16} /> : null}
                sx={{ backgroundColor: "#1F4E78" }}
                onClick={async () => {
                  setImporting(true);
                  setImportError(null);
                  try {
                    const res = await bulkImportDriversCsv(importFile, { sendInvitations: importSendInvitations });
                    setImportResult(unwrapObject(res));
                  } catch (e) {
                    setImportError(e?.response?.data?.message || e?.message || "Import failed");
                  } finally {
                    setImporting(false);
                  }
                }}
              >
                Import {importFile ? importFile.name : ""}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default AllDriversCompliancePage;
